import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { ExternalSettings } from '../../../services/external-settings/external-settings.interface';
import { ExternalSettingsService } from '../../../services/external-settings/external-settings.service';
import { AppAuthenticatorPushApprovalPayload } from '../app-authenticator-bean';
import { ResponsiveDesignService } from '../../../services/responsive-design/responsive-design.service';
import { UIUtilityService } from 'src/app/system/utility/ui-utility.service';
import { MFAScope } from '../../mfa/mfa-bean';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-app-authenticator-approval',
  templateUrl: './app-authenticator-approval.component.html',
  styleUrls: ['./app-authenticator-approval.component.less'],
  standalone: false,
})
export class AppAuthenticatorApprovalComponent implements OnInit {

  UIUtilityService: any = UIUtilityService;
  externalSettings!: ExternalSettings;
  formattedTimestamp = '';
  center: google.maps.LatLngLiteral | null = null;
  hasCoordinates = false;
  vizAliasTypeDisplay = '';
  currentStatusDisplay = '';
  newStatusDisplay = '';
  targetNameLabelKey = 'APP_AUTHENTICATOR.APPROVAL_TARGET_NAME';

  readonly mapOptions: google.maps.MapOptions = {
    clickableIcons: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    keyboardShortcuts: false,
    zoomControl: false,
    disableDefaultUI: true,
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AppAuthenticatorPushApprovalPayload,
    public dialogRef: MatDialogRef<AppAuthenticatorApprovalComponent, boolean>,
    private srvES: ExternalSettingsService,
    private translate: TranslateService,
    protected srvRpDesign: ResponsiveDesignService,
  ) { }

  ngOnInit(): void {
    this.srvES.externalSettings$.subscribe((settings) => {
      this.externalSettings = settings;
    });

    this.formattedTimestamp = this.formatTimestamp(this.data.timeStamp);
    this.hasCoordinates = this.data.latitude != null && this.data.longitude != null;
    if (this.hasCoordinates) {
      this.center = {
        lat: Number(this.data.latitude),
        lng: Number(this.data.longitude),
      };
    }

    this.targetNameLabelKey = this.data.scope === MFAScope.ADD_PEER
      ? 'APP_AUTHENTICATOR.APPROVAL_PEER_NAME'
      : 'APP_AUTHENTICATOR.APPROVAL_TARGET_NAME';
    this.vizAliasTypeDisplay = this.translateEnumValue(this.data.vizAliasType, 'VIZ.');
    this.currentStatusDisplay = this.translateEnumValue(this.data.currentStatus, 'VIZ.');
    this.newStatusDisplay = this.translateEnumValue(this.data.newStatus, 'VIZ.');
  }

  get deviceName(): string {
    return (this.data.deviceName || '').trim();
  }

  get isMapEnabled(): boolean {
    return !!(this.externalSettings?.globalVariables?.mapApiKey && this.hasCoordinates);
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onReject(): void {
    this.dialogRef.close(false);
  }

  private formatTimestamp(timeStamp: string): string {
    if (!timeStamp) {
      return '';
    }
    const dt = DateTime.fromFormat(timeStamp.trim(), 'dd/MM/yyyy HH:mm');
    return dt.isValid
      ? dt.toFormat("MMM dd, yyyy 'at' hh:mm a")
      : timeStamp;
  }

  /** Prefer VIZ.* i18n keys for known enum values; fall back to the raw value. */
  private translateEnumValue(value: string | null, prefix: string): string {
    if (!value) {
      return '';
    }
    const key = `${prefix}${value}`;
    const translated = this.translate.instant(key);
    return translated !== key ? translated : value;
  }
}
